import { Button } from '#GameFrontend/components/ui/button';
import Loading from '#GameFrontend/unauthorized/components/ui/Loading';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation } from 'react-router-dom';
import Login from './Login';
import PasswordReset from './PasswordReset';
// Import flags directly
import deFlag from '#GameFrontend/unauthorized/assets/flags/de.svg';
import gbFlag from '#GameFrontend/unauthorized/assets/flags/gb.svg';
import ruFlag from '#GameFrontend/unauthorized/assets/flags/ru.svg';

const languageFlags = {
	de: {
		flag: deFlag,
		name: 'Deutsch',
	},
	en: {
		flag: gbFlag,
		name: 'English',
	},
	ru: {
		flag: ruFlag,
		name: 'Русский',
	},
};

const LanguageSwitcher: React.FC = () => {
	const { i18n } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const [overlayPosition, setOverlayPosition] = useState({ top: 0, left: 0, width: 0 });

	const updatePosition = () => {
		if (buttonRef.current) {
			const rect = buttonRef.current.getBoundingClientRect();
			setOverlayPosition({
				top: rect.top + window.scrollY,
				left: rect.left,
				width: rect.width,
			});
		}
	};

	useEffect(() => {
		if (isOpen) {
			updatePosition();
			window.addEventListener('scroll', updatePosition);
			window.addEventListener('resize', updatePosition);
		}

		return () => {
			window.removeEventListener('scroll', updatePosition);
			window.removeEventListener('resize', updatePosition);
		};
	}, [isOpen]);

	const handleLanguageChange = async (lang: string) => {
		await i18n.changeLanguage(lang);
		setIsOpen(false);
	};

	const currentLanguage = languageFlags[i18n.language as keyof typeof languageFlags] || languageFlags.en;

	return (
		<div className="relative inline-block">
			<button
				ref={buttonRef}
				onClick={() => setIsOpen(!isOpen)}
				className="flex items-center space-x-2 px-3 py-2 rounded-md hover:bg-[rgba(255,255,255,0.1)] transition-colors"
			>
				<img src={currentLanguage.flag} alt={currentLanguage.name} className="w-6 h-4 object-cover rounded" />
			</button>
			{isOpen &&
				createPortal(
					<div
						className="fixed wood-light rounded-md shadow-lg py-2 min-w-[120px]"
						style={{
							position: 'absolute',
							top: `${overlayPosition.top - 4}px`,
							left: `${overlayPosition.left}px`,
							transform: 'translateY(-100%)',
							zIndex: 9999,
						}}
					>
						<div className="relative z-10">
							{Object.entries(languageFlags).map(([lang, { flag, name }]) => (
								<button
									key={lang}
									onClick={() => handleLanguageChange(lang)}
									className={`w-full flex items-center space-x-3 px-4 py-2 hover:bg-[rgba(255,255,255,0.1)] transition-colors ${
										i18n.language === lang ? 'bg-[rgba(255,255,255,0.1)]' : ''
									}`}
								>
									<img src={flag} alt={name} className="w-6 h-4 object-cover rounded" />
									<span className="uppercase text-white">{lang}</span>
								</button>
							))}
						</div>
					</div>,
					document.body
				)}
		</div>
	);
};

const Layout: React.FC = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const isIndex =
		location.pathname === '/' || location.pathname.startsWith('/verify') || location.pathname.startsWith('/reset');
	const isReset = location.pathname.startsWith('/reset/');
	const [showLoginForm, setShowLoginForm] = useState(isReset);

	useEffect(() => {
		setShowLoginForm(isReset);
	}, [isReset]);

	const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget) {
			setShowLoginForm(false);
		}
	};

	return (
		<main className="min-h-dvh xl:min-h-screen flex flex-col relative  overflow-hidden">
			<Loading />
			<div className="absolute top-[13%] left-[5%] right-0 bottom-[-13%] z-0 lg:hidden bg-mobile-image"></div>
			<svg width="0" height="0" xmlns="http://www.w3.org/2000/svg" className="opacity-0">
				<defs>
					<filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
						<feGaussianBlur in="SourceAlpha" stdDeviation="1.2" />
						<feOffset dx="0" dy="1.2" result="offsetblur" />
						<feComponentTransfer>
							<feFuncA type="linear" slope="0.8" />
						</feComponentTransfer>
						<feMerge>
							<feMergeNode />
							<feMergeNode in="SourceGraphic" />
						</feMerge>
					</filter>
				</defs>
			</svg>
			{/* Header with Logo and Login Form */}
			<header className="w-full relative z-30">
				{isIndex && (
					<Button
						className="w-full lg:hidden border-none rounded-none orange-solid-button h-[5%] lg:h-[50px] text-2xl lg:text-3xl"
						onClick={() => setShowLoginForm(!showLoginForm)}
					>
						{isReset ? t('password.reset.button') : t('login.button')}
					</Button>
				)}
				<div className="container mx-auto px-4 pt-2 xl:py-4 flex flex-col lg:flex-row lg:items-start lg:justify-between">
					{/* Logo */}
					<div className="flex items-center justify-center lg:justify-start mb-6 lg:mb-0">
						<Link to="/" className="cursor-pointer">
							<img
								src={require('#GameFrontend/unauthorized/assets/logo_big_with_alpha.png')}
								alt="Cavalcando Logo"
								className={`${isIndex ? 'lg:h-36 h-[20%]' : 'xl:h-20 h-14'} w-auto object-contain`}
							/>
						</Link>
					</div>

					{/*  Login Form (Desktop) */}
					<div className="w-full lg:w-auto">
						{isIndex ? (
							<div className="hidden lg:block">{isReset ? <PasswordReset /> : <Login />}</div>
						) : (
							<nav className="space-x-4"></nav>
						)}
					</div>
				</div>
			</header>

			{/* Form Overlay (Mobile) */}
			{showLoginForm && (
				<div
					className="fixed inset-0 bg-black bg-opacity-75 z-40 lg:hidden flex items-center justify-center"
					onClick={handleOverlayClick}
				>
					<div className="w-11/12 max-w-md">{isReset ? <PasswordReset /> : <Login />}</div>
				</div>
			)}

			{/* Main Content */}
			<div className="flex flex-grow">
				<Outlet />
			</div>
			{/* Footer */}
			<footer className="w-full wood-dark text-white py-4 relative z-20">
				<div className="container mx-auto px-4 flex justify-center items-center space-x-8 relative z-30">
					<LanguageSwitcher />
					<Link to="/impressum" className="cursor-pointer hover:text-gray-300 transition-colors">
						{t('legal.header')}
					</Link>
					<Link to="/dsgvo" className="cursor-pointer hover:text-gray-300 transition-colors">
						{t('privacy.header')}
					</Link>
					<Link to="/terms" className="cursor-pointer hover:text-gray-300 transition-colors">
						{t('terms.header')}
					</Link>
				</div>
			</footer>
		</main>
	);
};

export default Layout;
